import {GetStaticPaths, GetStaticProps} from 'next';

import {
  CollectionPageTemplate,
  CollectionPageTemplateProps,
} from 'src/components/collection-page';
import {Layout, LayoutProps} from 'src/components/layout/Layout';
import {regionalConfigsArr} from 'src/config';
import {PageParams} from 'src/types';
import {isAllowedRegion} from 'src/utils/contentful';
import {contentfulApi} from 'src/utils/contentful/client';

interface CollectionPageParams extends PageParams {
  collection_slug: string;
}

type CollectionPagePath = {params: CollectionPageParams};

type CollectionPageProps = LayoutProps & CollectionPageTemplateProps;

export const getStaticPaths: GetStaticPaths<
  CollectionPageParams
> = async () => {
  // locale doesn't matter here, we're just getting the slugs for path info
  const {collections} = await contentfulApi.getProductCollections();

  const paths: CollectionPagePath[] = collections.reduce((acc, curr) => {
    const pagePaths: CollectionPagePath[] = [];
    const {availableInRegions, slug} = curr.fields;

    regionalConfigsArr.forEach(({regionCode, locales}) => {
      if (isAllowedRegion(regionCode, availableInRegions)) {
        locales.forEach((locale) => {
          pagePaths.push({
            params: {region: regionCode, locale, collection_slug: slug},
          });
        });
      }
    });

    return [...acc, ...pagePaths];
  }, [] as CollectionPagePath[]);

  return {paths, fallback: false};
};

export const getStaticProps: GetStaticProps<
  CollectionPageProps,
  CollectionPageParams
> = async ({params, preview}) => {
  if (!params) {
    throw new Error(`Could not find valid params for collections index page`);
  }

  const {region, locale, collection_slug} = params;

  const api = preview ? contentfulApi.preview : contentfulApi;

  const {collections} = await api.getProductCollections({
    locale,
    'fields.slug': collection_slug,
  });

  const collection = collections[0];
  const {fields} = collection;
  const {products, name, seoDescription, availableInRegions} = fields;

  const regionalProducts = products.filter(({fields: productFields}) => {
    const isAvailableInRegion = isAllowedRegion(
      region,
      productFields.availableInRegions
    );
    return isAvailableInRegion && !productFields.hidden;
  });

  return {
    props: {
      collection,
      products: regionalProducts,
      seo: {
        slug: collection_slug,
        title: name,
        description: seoDescription,
        slugPrefix: 'collections',
        includeInRegions: availableInRegions,
        noIndex: false,
      },
      pageContext: {
        region,
        locale,
      },
      preview: !!preview,
    },
  };
};

const CollectionPage: React.FC<React.PropsWithChildren<CollectionPageProps>> = (
  props
) => {
  const {seo, pageContext, collection, products, preview} = props;

  return (
    <Layout seo={seo} pageContext={pageContext} preview={preview}>
      <CollectionPageTemplate collection={collection} products={products} />
    </Layout>
  );
};

export default CollectionPage;
